import './index.css';
import './i18n'; // Initialize i18n
import NavBar from './components/blocks/nav';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useState, useEffect } from 'react';
import DBTable from './handlers/DatabaseHandler';
import { AuthProvider, useAuth, isNotPresenter, isStaffOrPresenter, isVerifiedSchoolStaff, isPresenter } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import Footer from './components/blocks/Footer';
import { useTranslation } from 'react-i18next';
import { loadLanguageFromProfile, normalizeLanguage, resolveLanguagePreference } from './utils/languageUtils';
import { useLanguageSync } from './hooks/useLanguageSync';
import LoadingSpinner from './components/blocks/LoadingSpinner';
import useScrollToTop from './hooks/useScrollToTop';
import { COLLECTION } from './config/mongoCollections.js';

import Home from './pages/Home';
import ViewReport from './pages/ViewReport';
import Help from './pages/Help';
import AbilityTest from './pages/PersonalityTest';
import Chatbot from './pages/ChatBot';
import EddyBeta from './pages/EddyBeta';
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import EditProfile from './pages/EditProfile';
import TrajectoryMap from './pages/TrajectoryMap';
import Presentation from './pages/Presentation';
import PressReleases from './pages/PressReleases';
import PressReleaseDetail from './pages/PressReleaseDetail';
import Hiring from './pages/Hiring';
import MeetTheTeam from './pages/MeetTheTeam';
import HiringDetail from './pages/HiringDetail';
import NotFound from './pages/NotFound';
import JUPASelect from './pages/JUPASelect';
import JUPASCalculator from './pages/JUPASCalculator';
import JupasSurveyHome from './pages/JupasSurveyHome';
import JupasSurveySubmission from './pages/JupasSurveySubmission';
import JupasSurveySuccess from './pages/JupasSurveySuccess';
import PresentationAnalytics from './pages/PresentationAnalytics';
import JupasSurvey from './pages/JupasSurvey';
import JupasSurveyResults from './pages/JupasSurveyResults';
import JupasPublicResults from './pages/JupasPublicResults';
import Privacy from './pages/Privacy';
import TestResults from './pages/TestResults';
import InterestTest from './pages/InterestTest';
import PromotionalHomeLegacyBeta from './pages/PromotionalHomePreview';
import JoinSchool from './pages/JoinSchool';
import JoinFeatureAccess from './pages/JoinFeatureAccess';
import SchoolDashboard from './pages/SchoolDashboard';
import BetaSchoolAdmin from './pages/BetaSchoolAdmin';
import BetaFeatureInvites from './pages/BetaFeatureInvites';
import BugReports from './pages/BugReports';
import HkuArtsDemo from './pages/HkuArtsDemo';
// Protected Route wrapper component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const location = useLocation();
  if (!isAuthenticated) {
    const returnTo = `${location.pathname}${location.search || ''}`;
    const loginPath =
      returnTo && returnTo !== '/'
        ? `/login?returnTo=${encodeURIComponent(returnTo)}`
        : '/login';
    return <Navigate to={loginPath} replace />;
  }
  return children;
};

// Special protected route for Presentation page
const PresentationRoute = ({ children }) => {
  const { currentUser } = useAuth();
  if (!currentUser || isNotPresenter(currentUser)) {
    return <Navigate to="/" replace />;
  }
  return children;
};

const StaffOrPresenterRoute = ({ children }) => {
  const { currentUser } = useAuth();
  if (!currentUser || !isStaffOrPresenter(currentUser)) {
    return <Navigate to="/" replace />;
  }
  return children;
};

const SchoolStaffOrPresenterRoute = ({ children }) => {
  const { currentUser, isAuthenticated } = useAuth();
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  if (!currentUser || (!isVerifiedSchoolStaff(currentUser) && !isPresenter(currentUser))) {
    return <Navigate to="/" replace />;
  }
  return children;
};

// Scroll to top component for route changes
const ScrollToTop = () => {
  // Use the custom hook with improved configuration
  useScrollToTop({
    enabled: true,
    behavior: 'auto',
    delay: 10, // Small delay to ensure DOM is ready
    excludePaths: ['/trajectory'], // Exclude trajectory page as it has its own scroll management
    retryAttempts: 3, // Retry up to 3 times if scroll fails
    retryDelay: 100 // Wait 100ms between retries
  });

  return null;
};

// Language initialization component
const LanguageInitializer = ({ children }) => {
  const { i18n } = useTranslation();
  const { isAuthenticated, currentUser, loading } = useAuth();
  const [isLanguageReady, setIsLanguageReady] = useState(false);
  const [hasReloaded, setHasReloaded] = useState(false);
  const [hasCompleted, setHasCompleted] = useState(false);

  useEffect(() => {
    const initializeLanguage = async () => {
      try {
        // Don't run if already completed
        if (hasCompleted) {
          // console.log('LanguageInitializer: Already completed, skipping...');
          return;
        }

        // Wait for authentication context to be ready
        if (loading) {
          // console.log('LanguageInitializer: Waiting for auth context to load...');
          return;
        }

        // console.log('LanguageInitializer: Auth context ready. isAuthenticated:', isAuthenticated, 'currentUser:', currentUser?.User_ID);

        // Check if this is the first load or if language needs to be synchronized
        const isFirstLoad = !localStorage.getItem('languageInitialized');
        const currentLanguage = normalizeLanguage(i18n.language) || 'en';

        // console.log('LanguageInitializer: isFirstLoad:', isFirstLoad, 'currentLanguage:', currentLanguage);

        if (isFirstLoad && !hasReloaded) {
          // Mark that we've attempted initialization
          setHasReloaded(true);

          // Wait for i18n to be ready
          if (!i18n.isInitialized) {
            // console.log('LanguageInitializer: Waiting for i18n to initialize...');
            await new Promise(resolve => {
              const checkInitialized = () => {
                if (i18n.isInitialized) {
                  resolve();
                } else {
                  setTimeout(checkInitialized, 100);
                }
              };
              checkInitialized();
            });
          }

          // Load user language preference if authenticated
          let targetLanguage = 'en';
          if (isAuthenticated && currentUser?.User_ID) {
            try {
              const userTable = new DBTable(COLLECTION.users, "User_ID", {});
              const userData = await userTable.handleRead({ User_ID: currentUser.User_ID }, false);
              targetLanguage = await loadLanguageFromProfile(userData);
            } catch (error) {
              console.error('Error loading user language preference:', error);
              targetLanguage = resolveLanguagePreference({
                dbLanguage: currentUser?.preferred_language
              });
            }
          } else {
            targetLanguage = resolveLanguagePreference({
              localLanguage: localStorage.getItem('i18nextLng')
            });
            // If nothing stored yet, fall back to browser language
            if (!localStorage.getItem('i18nextLng')) {
              const browserLang = navigator.language || navigator.userLanguage || '';
              if (browserLang.toLowerCase().startsWith('zh')) {
                targetLanguage = browserLang.includes('CN') || browserLang.includes('Hans') ? 'sc' : 'tc';
              }
            }
          }

          // If the current language doesn't match the target language, reload
          if (currentLanguage !== targetLanguage) {
            // console.log(`LanguageInitializer: Language mismatch detected. Current: ${currentLanguage}, Target: ${targetLanguage}. Reloading...`);
            await i18n.changeLanguage(targetLanguage);
            // Force a page reload to ensure all components render with the correct language
            window.location.reload();
            return;
          } else {
            // Languages match, mark as completed
            // console.log(`LanguageInitializer: Languages match (${currentLanguage}), initialization complete`);
            localStorage.setItem('languageInitialized', 'true');
            setHasCompleted(true);
            setIsLanguageReady(true);
            return;
          }
        } else {
          // Not first load, mark as completed
          // console.log('LanguageInitializer: Not first load, marking as completed');
          setHasCompleted(true);
          setIsLanguageReady(true);
        }

        // Set language as ready
        setIsLanguageReady(true);
      } catch (error) {
        console.error('Error during language initialization:', error);
        // Set as ready even if there's an error to prevent infinite loading
        setHasCompleted(true);
        setIsLanguageReady(true);
      }
    };

    initializeLanguage();
  }, [i18n, isAuthenticated, currentUser, hasReloaded, loading, hasCompleted]);

  // Show loading spinner while language is initializing or auth is loading
  if (!isLanguageReady || loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-canvas">
        <div className="text-center">
          <LoadingSpinner size="large" text={loading ? 'Loading user preferences...' : 'Initializing language preferences...'} />
        </div>
      </div>
    );
  }

  return children;
};

// Main content component that uses useLocation
const MainContent = () => {
  const location = useLocation();
  const { isAuthenticated, currentUser, logout, login, register } = useAuth();
  const { isSynced } = useLanguageSync(); // Add language synchronization
  const isTrajectoryPage = location.pathname === '/trajectory';
  const isEddyPage = location.pathname === '/eddy' || location.pathname === '/beta/eddy';
  const isPromotionalLayout = location.pathname === '/';
  const isTeamPage = location.pathname === '/team';
  const isHkuArtsPage = location.pathname === '/demo';
  const navVariant = isPromotionalLayout ? 'promotional' : 'default';
  const shouldShowFooter = !isTrajectoryPage && !isEddyPage && !isHkuArtsPage;
  const mainTopPadding = isHkuArtsPage
    ? ''
    : navVariant === 'promotional'
      ? 'pt-[5.6rem] sm:pt-[6.4rem]'
      : 'pt-16';
  const shellBg = isPromotionalLayout ? 'bg-canvas-promo' : 'bg-canvas';
  const loginReturnTo = (() => {
    const raw = new URLSearchParams(location.search).get('returnTo');
    if (!raw || !raw.startsWith('/') || raw.startsWith('//')) return '/';
    return raw;
  })();

  return (
    <div className={`flex flex-col min-h-screen text-ink ${shellBg}`}>
      {!isHkuArtsPage && (
        <NavBar
          variant={navVariant}
          isAuthenticated={isAuthenticated}
          userData={currentUser}
          onLogout={logout}
        />
      )}
      <ScrollToTop />
      <div className={`flex-1 flex flex-col ${isTeamPage ? 'min-h-0 overflow-hidden' : ''}`}>
        <main
          id="app-main"
          className={`flex-1 ${mainTopPadding} ${isTeamPage ? 'flex min-h-0 flex-col overflow-hidden' : ''}`}
        >
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/login" element={
              isAuthenticated ?
                <Navigate to={loginReturnTo} replace /> :
                <Login onLogin={login} />
            } />
            <Route path="/register" element={
              isAuthenticated ?
                <Navigate to="/" replace /> :
                <Register onRegister={register} />
            } />
            <Route path="/join/:code" element={<JoinSchool />} />
            <Route path="/access/:code" element={<JoinFeatureAccess />} />
            <Route path="/forgot-password" element={
              isAuthenticated ?
                <Navigate to="/" replace /> :
                <ForgotPassword />
            } />
            <Route path="/reset-password" element={
              isAuthenticated ?
                <Navigate to="/" replace /> :
                <ResetPassword />
            } />
            <Route path="/view-report" element={
              <ProtectedRoute>
                <ViewReport />
              </ProtectedRoute>
            } />
            <Route path="/tutorials" element={<Help />} />
            <Route path="/help" element={<Navigate to="/tutorials" replace />} />
            <Route path="/help-centre" element={<Navigate to="/tutorials" replace />} />
            <Route path="/help-center" element={<Navigate to="/tutorials" replace />} />
            <Route path="/press-releases" element={<PressReleases />} />
            <Route path="/press-releases/:slug" element={<PressReleaseDetail />} />
            <Route path="/hiring" element={<Hiring />} />
            <Route path="/hiring/:slug" element={<HiringDetail />} />
            <Route path="/team" element={<MeetTheTeam />} />
            <Route path="/test" element={<Navigate to="/ability-test" replace />} />
            <Route path="/chatbot" element={<Navigate to="/eddy" replace />} />
            <Route path="/jupa-select" element={<Navigate to="/jupas-calculator" replace />} />
            <Route path="/jupaselect" element={<Navigate to="/jupas-calculator" replace />} />
            <Route path="/ability-test" element={
              <ProtectedRoute>
                <AbilityTest />
              </ProtectedRoute>
            } />
            <Route path="/interest-test" element={
              <ProtectedRoute>
                <InterestTest />
              </ProtectedRoute>
            } />
            <Route path="/beta/eddy" element={
              <ProtectedRoute>
                <EddyBeta />
              </ProtectedRoute>
            } />
            <Route path="/eddy" element={
              <PresentationRoute>
                <Chatbot />
              </PresentationRoute>
            } />
            <Route path="/edit-profile" element={<EditProfile />} />
            <Route path="/trajectory" element={
              <PresentationRoute>
                <TrajectoryMap />
              </PresentationRoute>
            } />
            <Route path="/presentation" element={
              <PresentationRoute>
                <Presentation />
              </PresentationRoute>
            } />
            <Route path="/analytics" element={
              <PresentationRoute>
                <PresentationAnalytics />
              </PresentationRoute>
            } />
            <Route path="/test-results" element={
              <StaffOrPresenterRoute>
                <TestResults />
              </StaffOrPresenterRoute>
            } />
            <Route path="/school" element={
              <SchoolStaffOrPresenterRoute>
                <SchoolDashboard />
              </SchoolStaffOrPresenterRoute>
            } />
            <Route path="/jupas-calculator" element={<JUPASCalculator />} />
            <Route path="/jupas-survey" element={<JupasSurveyHome />} />
            <Route path="/jupas-survey/submit" element={<JupasSurveySubmission />} />
            <Route path="/jupas-survey/success" element={<JupasSurveySuccess />} />
            <Route path="/jupas-results" element={<JupasPublicResults />} />
            <Route path="/beta/jupaselect" element={
              <PresentationRoute>
                <JUPASelect />
              </PresentationRoute>
            } />
            <Route path="/beta/schools" element={
              <PresentationRoute>
                <BetaSchoolAdmin />
              </PresentationRoute>
            } />
            <Route path="/beta/feature-invites" element={
              <PresentationRoute>
                <BetaFeatureInvites />
              </PresentationRoute>
            } />
            <Route path="/bug-reports" element={
              <PresentationRoute>
                <BugReports />
              </PresentationRoute>
            } />
            <Route path="/jupasurvey-results" element={
              <PresentationRoute>
                <JupasSurveyResults />
              </PresentationRoute>
            } />
            <Route path="/about" element={<Navigate to="/" replace />} />
            <Route path="/jupasurvey" element={<JupasSurvey />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/demo" element={<HkuArtsDemo />} />
            <Route path="/promotional-home" element={<Navigate to="/" replace />} />
            <Route
              path="/beta/promo-home"
              element={
                <PresentationRoute>
                  <PromotionalHomeLegacyBeta />
                </PresentationRoute>
              }
            />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </main>
        {shouldShowFooter && (
          <div className="mt-auto">
            <Footer />
          </div>
        )}
      </div>
    </div>
  );
};

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <Router>
          <LanguageInitializer>
            <MainContent />
          </LanguageInitializer>
        </Router>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
